import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tv, Star, Clock, ListVideo, Search, Play, RefreshCw, LogOut, Plus,
  Trash2, Globe, Loader2, X, Radio, Film, Filter, Eye, Languages, Monitor,
} from "lucide-react";
import { Logo } from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import VideoPlayer from "../components/VideoPlayer";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFavorites, useRecent } from "../hooks/useLocalData";

const PRESETS = [
  { name: "iptv-org · All channels (Global)", url: "https://iptv-org.github.io/iptv/index.m3u" },
  { name: "United States", url: "https://iptv-org.github.io/iptv/countries/us.m3u" },
  { name: "United Kingdom", url: "https://iptv-org.github.io/iptv/countries/uk.m3u" },
  { name: "Spain", url: "https://iptv-org.github.io/iptv/countries/es.m3u" },
  { name: "News", url: "https://iptv-org.github.io/iptv/categories/news.m3u" },
  { name: "Sports", url: "https://iptv-org.github.io/iptv/categories/sports.m3u" },
  { name: "Music", url: "https://iptv-org.github.io/iptv/categories/music.m3u" },
];

const NAV = [
  { id: "livetv", label: "Live TV", icon: Tv },
  { id: "favorites", label: "Favorites", icon: Star },
  { id: "recent", label: "Recently Watched", icon: Clock },
  { id: "lists", label: "My Lists", icon: ListVideo },
];

export default function Player() {
  const nav = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const previewMode = !user && isAdmin;
  const email = user?.email || (previewMode ? "admin@preview.local" : "guest");
  const { favs, isFav, toggle } = useFavorites(email);
  const { recent, push, clear } = useRecent(email);

  const [page, setPage] = useState("livetv");
  const [lists, setLists] = useState([]);
  const [activeUrl, setActiveUrl] = useState("");
  const [data, setData] = useState({ channels: [], groups: [], countries: [], languages: [], qualities: [] });
  const [epg, setEpg] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");
  const [err, setErr] = useState("");
  const [playing, setPlaying] = useState(null);

  const [q, setQ] = useState("");
  const [group, setGroup] = useState("All");
  const [country, setCountry] = useState("All");
  const [language, setLanguage] = useState("All");
  const [quality, setQuality] = useState("All");

  const loadLists = useCallback(async () => {
    try {
      const { data } = await api.get("/lists");
      setLists(data.lists);
      return data.lists;
    } catch (e) { setErr(errMsg(e)); return []; }
  }, []);

  const loadChannels = useCallback(async (url) => {
    if (!url) return;
    setActiveUrl(url);
    setLoading(true);
    setErr("");
    setLoadMsg("Downloading playlist…");
    setQ(""); setGroup("All"); setCountry("All"); setLanguage("All"); setQuality("All");
    try {
      const { data } = await api.get(`/iptv/parse?url=${encodeURIComponent(url)}`);
      setData(data);
      setEpg({});
      if (data.epg_url) {
        const ids = [...new Set(data.channels.map((c) => c.tvg_id).filter(Boolean))];
        if (ids.length) {
          api.post("/iptv/epg", { epg_url: data.epg_url, ids })
            .then((r) => setEpg(r.data.epg || {}))
            .catch(() => {});
        }
      }
    } catch (e) {
      setErr(errMsg(e));
      setData({ channels: [], groups: [], countries: [], languages: [], qualities: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const l = await loadLists();
      if (l.length) loadChannels(l[0].url);
    })();
  }, [loadLists, loadChannels]);

  const play = (ch) => { setPlaying(ch); push(ch); };
  const doLogout = () => {
    if (previewMode) { nav("/admin"); return; }
    logout();
    nav("/login");
  };

  const filtered = useMemo(() => {
    let list = page === "favorites" ? favs : page === "recent" ? recent : data.channels;
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(s) || (c.group || "").toLowerCase().includes(s) || (c.country || "").toLowerCase().includes(s) || (c.language || "").toLowerCase().includes(s));
    }
    // group-title is compound ("Animation;Kids;Music"), so match against the split
    // categories the API now returns; fall back to the raw group for older payloads.
    if (group !== "All") list = list.filter((c) => (c.categories?.length ? c.categories.includes(group) : c.group === group));
    if (country !== "All") list = list.filter((c) => c.country === country);
    if (language !== "All") list = list.filter((c) => c.language === language);
    if (quality !== "All") list = list.filter((c) => c.quality === quality);
    return list;
  }, [page, favs, recent, data.channels, q, group, country, language, quality]);

  const activeFilters = [group, country, language, quality].filter((v) => v !== "All").length;

  return (
    <div className="min-h-screen bg-tv-darkest flex text-tv-text">
      {/* Sidebar */}
      <aside className="w-[220px] shrink-0 border-r border-tv-border bg-tv-darkest flex flex-col fixed h-screen">
        <div className="px-5 pt-5 pb-3"><Logo size={21} /></div>
        <nav className="mt-3 flex-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setPage(n.id)}
              data-testid={`nav-${n.id}`}
              className={`w-full flex items-center gap-3 mx-0 px-5 py-3 text-sm font-semibold transition-colors ${
                page === n.id ? "bg-tv-card text-tv-cyan border-l-[3px] border-tv-cyan pl-[17px]" : "text-tv-sub hover:bg-tv-hover hover:text-tv-text"
              }`}
            >
              <n.icon size={18} />
              {n.label}
              {n.id === "favorites" && favs.length > 0 && (
                <span className="ml-auto text-[10px] bg-tv-active text-tv-cyan px-2 py-0.5 rounded-full">{favs.length}</span>
              )}
            </button>
          ))}

          <div className="px-5 mt-6 mb-2 text-[9px] uppercase tracking-[2.5px] text-tv-muted font-bold">Ecosystem</div>
          <div className="px-5 space-y-2">
            <EcoItem icon={Tv} color="#22D3EE" label="TVVerse" note="Installed" active />
            <EcoItem icon={Radio} color="#38BDF8" label="RadioVerse" note="Soon" />
            <EcoItem icon={Film} color="#FB923C" label="ClassicVerse" note="Soon" />
          </div>
        </nav>

        <div className="p-4 border-t border-tv-border">
          <div className="flex items-center gap-2.5 mb-3 px-1">
            <div className="w-8 h-8 rounded-full tv-gradient flex items-center justify-center text-[#04201c] font-extrabold text-sm">
              {(user?.username || email)[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.username || (previewMode ? "Admin (preview)" : "You")}</div>
              <div className="text-[11px] text-tv-muted truncate">{email}</div>
            </div>
          </div>
          <button onClick={doLogout} className="btn-ghost w-full flex items-center justify-center gap-2 text-sm" data-testid="logout-button">
            <LogOut size={15} /> {previewMode ? "Back to admin" : "Log out"}
          </button>
          <ThemeToggle className="w-full mt-2" />
          <div className="text-[10px] text-tv-muted text-center mt-3">v1.5 · © 2026 MediaVerse Labs</div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-[220px] p-8 min-h-screen">
        {previewMode && (
          <div className="mb-5 flex items-center justify-between bg-tv-orange/10 border border-tv-orange/30 rounded-xl px-4 py-3" data-testid="preview-banner">
            <span className="text-sm text-tv-orange font-semibold flex items-center gap-2">
              <Eye size={16} /> Preview mode: you're seeing the app as a regular user
            </span>
            <button onClick={() => nav("/admin")} className="btn-ghost text-sm py-1.5" data-testid="exit-preview-button">
              Back to admin
            </button>
          </div>
        )}
        {err && (
          <div className="mb-5 text-sm text-tv-red bg-tv-red/10 border border-tv-red/30 rounded-xl px-4 py-3 flex items-center justify-between" data-testid="player-error-banner">
            <span>{err}</span>
            <button onClick={() => setErr("")}><X size={15} /></button>
          </div>
        )}

        {page === "lists" ? (
          <MyLists lists={lists} reload={loadLists} onActivate={(url) => { setPage("livetv"); loadChannels(url); }} activeUrl={activeUrl} />
        ) : (
          <>
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-extrabold tracking-tight">
                  {page === "livetv" ? "Live TV" : page === "favorites" ? "My Favorites" : "Recently Watched"}
                </h1>
                <p className="text-tv-sub text-sm mt-1">
                  {page === "livetv"
                    ? `${data.channels.length.toLocaleString()} channels`
                    : page === "favorites"
                    ? `${favs.length} saved channels`
                    : `${recent.length} recently played`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {page === "livetv" && (
                  <>
                    <select value={activeUrl} onChange={(e) => loadChannels(e.target.value)} className="field w-auto min-w-[200px] cursor-pointer" data-testid="playlist-select">
                      {lists.length === 0 && <option value="">No playlists. Add one in My Lists</option>}
                      {lists.map((l) => (
                        <option key={l.id} value={l.url}>{l.is_global ? "★ " : ""}{l.name}</option>
                      ))}
                    </select>
                    <button onClick={() => loadChannels(activeUrl)} disabled={loading || !activeUrl} className="btn-primary flex items-center gap-2" data-testid="refresh-channels-button">
                      <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                  </>
                )}
                {page === "recent" && recent.length > 0 && (
                  <button onClick={clear} className="btn-ghost flex items-center gap-2" data-testid="clear-recent-button"><Trash2 size={15} /> Clear</button>
                )}
              </div>
            </div>

            {/* Continue watching */}
            {page === "livetv" && recent.length > 0 && !loading && (
              <button
                onClick={() => play(recent[0])}
                className="card w-full flex items-center gap-4 p-4 mb-6 text-left group hover:border-tv-teal transition-colors"
                data-testid="continue-watching"
              >
                <div className="w-24 h-14 rounded-lg bg-tv-medium border border-tv-border flex items-center justify-center overflow-hidden shrink-0">
                  {recent[0].logo ? (
                    <img src={recent[0].logo} alt="" className="max-w-[75%] max-h-[75%] object-contain" onError={(e) => (e.target.style.display = "none")} />
                  ) : (
                    <Tv size={22} className="text-tv-muted" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-tv-teal font-bold mb-0.5">Continue watching</div>
                  <div className="font-bold truncate">{recent[0].name}</div>
                  <div className="text-xs text-tv-muted truncate">{recent[0].group}{recent[0].country ? ` · ${recent[0].country}` : ""}</div>
                </div>
                <span className="btn-primary flex items-center gap-2 shrink-0 group-hover:brightness-110">
                  <Play size={16} fill="currentColor" /> Resume
                </span>
              </button>
            )}

            {/* Filters */}
            {(page === "livetv" || filtered.length > 8) && (
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                  <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-tv-muted" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search channels…" className="field pl-10" data-testid="channel-search-input" />
                </div>
                {page === "livetv" && (
                  <>
                    <FilterSelect value={group} onChange={setGroup} options={["All", ...(data.groups || [])]} icon={Filter} allLabel="All categories" testid="group-filter" />
                    <FilterSelect value={country} onChange={setCountry} options={["All", ...(data.countries || [])]} icon={Globe} allLabel="All countries" testid="country-filter" />
                    <FilterSelect value={language} onChange={setLanguage} options={["All", ...(data.languages || [])]} icon={Languages} allLabel="All languages" testid="language-filter" />
                    <FilterSelect value={quality} onChange={setQuality} options={["All", ...(data.qualities || [])]} icon={Monitor} allLabel="Any quality" testid="quality-filter" />
                    {activeFilters > 0 && (
                      <button
                        onClick={() => { setGroup("All"); setCountry("All"); setLanguage("All"); setQuality("All"); }}
                        className="btn-ghost text-sm py-2 flex items-center gap-1.5"
                        data-testid="clear-filters-button"
                      >
                        <X size={14} /> Clear {activeFilters}
                      </button>
                    )}
                    <span className="text-xs text-tv-muted ml-auto" data-testid="filter-result-count">
                      {filtered.length.toLocaleString()} of {(data.channels || []).length.toLocaleString()} channels
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Content */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-32 text-tv-sub gap-3">
                <Loader2 size={34} className="animate-spin text-tv-teal" />
                <span>{loadMsg || "Loading…"}</span>
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState page={page} hasLists={lists.length > 0} onGoLists={() => setPage("lists")} />
            ) : (
              <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(230px,1fr))]" data-testid="channel-grid">
                {filtered.slice(0, 600).map((ch) => (
                  <ChannelCard key={ch.uid} ch={ch} onPlay={play} isFav={isFav(ch.uid)} onFav={() => toggle(ch)} epg={ch.tvg_id ? epg[ch.tvg_id] : null} />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {playing && <VideoPlayer channel={playing} epg={playing.tvg_id ? epg[playing.tvg_id] : null} onClose={() => setPlaying(null)} />}
    </div>
  );
}

function EcoItem({ icon: Icon, color, label, note, active }) {
  return (
    <div className={`flex items-center gap-2.5 text-xs ${active ? "" : "opacity-55"}`}>
      <Icon size={15} style={{ color }} />
      <span className="font-semibold text-tv-sub">{label}</span>
      <span className={`ml-auto text-[9px] font-bold uppercase tracking-wider ${active ? "text-tv-green" : "text-tv-muted"}`}>{note}</span>
    </div>
  );
}

function FilterSelect({ value, onChange, options, icon: Icon, allLabel, testid }) {
  // A filter with nothing but "All" in it is noise, so hide it: the playlist has no
  // data for that dimension.
  if (options.length <= 1) return null;
  const active = value !== "All";
  return (
    <div className="relative">
      <Icon size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${active ? "text-tv-cyan" : "text-tv-muted"}`} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`field pl-9 pr-8 w-auto max-w-[220px] cursor-pointer appearance-none ${active ? "border-tv-cyan/50 text-tv-cyan" : ""}`}
        data-testid={testid}
      >
        {options.map((o) => <option key={o} value={o}>{o === "All" ? allLabel : o}</option>)}
      </select>
    </div>
  );
}

function ChannelCard({ ch, onPlay, isFav, onFav, epg }) {
  const [imgOk, setImgOk] = useState(!!ch.logo);
  const now = epg?.now?.title;
  return (
    <div className="card p-3.5 group hover:border-tv-borderLight hover:bg-tv-hover transition-colors relative" data-testid="channel-card">
      <button
        onClick={onFav}
        className={`absolute top-2.5 right-2.5 z-10 p-1.5 rounded-lg transition-colors ${isFav ? "text-tv-yellow" : "text-tv-muted hover:text-tv-yellow"}`}
        data-testid="favorite-toggle"
        title={isFav ? "Remove favorite" : "Add favorite"}
      >
        <Star size={16} fill={isFav ? "currentColor" : "none"} />
      </button>

      <div className="w-full aspect-video rounded-xl bg-tv-medium border border-tv-border flex items-center justify-center overflow-hidden mb-3 relative">
        {imgOk ? (
          // Logos come from ~600 different third-party hosts; without lazy loading the
          // grid fires every request at once and the page stalls behind them.
          <img src={ch.logo} alt="" loading="lazy" decoding="async" className="max-w-[70%] max-h-[70%] object-contain" onError={() => setImgOk(false)} />
        ) : (
          <Tv size={30} className="text-tv-muted" />
        )}
        <button
          onClick={() => onPlay(ch)}
          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid="play-channel-button"
        >
          <span className="w-12 h-12 rounded-full bg-tv-green flex items-center justify-center text-[#04201c] shadow-lg">
            <Play size={22} fill="currentColor" className="ml-0.5" />
          </span>
        </button>
      </div>

      <div className="font-semibold text-sm truncate" title={ch.name}>{ch.name}</div>
      <div className="text-xs text-tv-muted truncate mt-0.5 flex items-center gap-1.5">
        {ch.flag && <span>{ch.flag}</span>}
        <span className="truncate">{ch.group}{ch.country ? ` · ${ch.country}` : ""}</span>
      </div>
      {now && (
        <div className="text-[11px] text-tv-teal truncate mt-1 flex items-center gap-1" title={now} data-testid="epg-now">
          <span className="w-1.5 h-1.5 rounded-full bg-tv-teal animate-pulseDot shrink-0" />
          {now}
        </div>
      )}
    </div>
  );
}

function EmptyState({ page, hasLists, onGoLists }) {
  const map = {
    livetv: { icon: Tv, title: hasLists ? "No channels to show" : "Add a playlist to start", sub: hasLists ? "Try a different playlist or refresh." : "Go to My Lists and add your first M3U playlist." },
    favorites: { icon: Star, title: "No favorites yet", sub: "Tap the star on any channel to save it here." },
    recent: { icon: Clock, title: "Nothing watched yet", sub: "Channels you play will appear here." },
  };
  const s = map[page];
  return (
    <div className="flex flex-col items-center justify-center py-28 text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl bg-tv-card border border-tv-border flex items-center justify-center text-tv-muted mb-4">
        <s.icon size={28} />
      </div>
      <div className="text-lg font-bold">{s.title}</div>
      <div className="text-tv-sub text-sm mt-1 max-w-sm">{s.sub}</div>
      {page === "livetv" && !hasLists && (
        <button onClick={onGoLists} className="btn-primary mt-5 flex items-center gap-2"><Plus size={16} /> Add playlist</button>
      )}
    </div>
  );
}

/* ── My Lists page ─────────────────────────── */
function MyLists({ lists, reload, onActivate, activeUrl }) {
  const [form, setForm] = useState({ name: "", url: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await api.post("/lists", { ...form, list_type: "url" }); setForm({ name: "", url: "" }); await reload(); }
    catch (e2) { setErr(errMsg(e2)); }
    finally { setBusy(false); }
  };
  const addPreset = async (p) => {
    setErr(""); setBusy(true);
    try { await api.post("/lists", { name: p.name, url: p.url, list_type: "url" }); await reload(); onActivate(p.url); }
    catch (e2) { setErr(errMsg(e2)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/lists/${id}`); await reload(); }
    catch (e) { setErr(errMsg(e)); }
  };

  return (
    <div className="animate-fadeIn max-w-4xl">
      <h1 className="text-3xl font-extrabold tracking-tight">My Lists</h1>
      <p className="text-tv-sub text-sm mt-1 mb-6">
        Add your own IPTV playlist by URL. Click a playlist to load it in Live TV.
      </p>

      {err && <div className="mb-4 text-sm text-tv-red bg-tv-red/10 border border-tv-red/30 rounded-xl px-4 py-3">{err}</div>}

      <form onSubmit={add} className="card p-5 mb-6 flex flex-col sm:flex-row gap-3" data-testid="add-list-form">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Playlist name" className="field sm:w-52" data-testid="list-name-input" />
        <input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…/playlist.m3u" className="field flex-1" data-testid="list-url-input" />
        <button type="submit" disabled={busy} className="btn-primary flex items-center gap-2" data-testid="add-list-button"><Plus size={17} /> Add</button>
      </form>

      {/* Quick presets */}
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-tv-muted mb-3">Quick add · iptv-org free playlists</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.url} onClick={() => addPreset(p)} disabled={busy} className="text-xs font-semibold bg-tv-card border border-tv-border hover:border-tv-teal hover:text-tv-cyan text-tv-sub rounded-full px-3.5 py-1.5 transition-colors flex items-center gap-1.5" data-testid="preset-add">
              <Plus size={12} /> {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Existing lists */}
      <div className="space-y-3" data-testid="my-lists">
        {lists.length === 0 ? (
          <div className="card p-8 text-center text-tv-muted">No playlists yet. Add one above or pick a quick playlist.</div>
        ) : (
          lists.map((l) => (
            <div key={l.id} className={`card px-5 py-4 flex items-center justify-between cursor-pointer transition-colors hover:border-tv-borderLight ${activeUrl === l.url ? "!border-tv-teal" : ""}`} onClick={() => onActivate(l.url)} data-testid={`list-item-${l.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-tv-medium border border-tv-border flex items-center justify-center text-tv-cyan shrink-0">
                  {l.is_global ? <Globe size={18} /> : <ListVideo size={18} />}
                </div>
                <div className="min-w-0">
                  <div className="font-bold flex items-center gap-2 truncate">
                    {l.name}
                    {l.is_global && <span className="text-[9px] uppercase tracking-wider text-tv-teal bg-tv-teal/10 px-2 py-0.5 rounded-full">Global</span>}
                  </div>
                  <div className="text-tv-muted text-xs truncate max-w-md">{l.url}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={(e) => { e.stopPropagation(); onActivate(l.url); }} className="btn-ghost p-2" title="Load" data-testid={`load-list-${l.id}`}><Play size={15} /></button>
                {!l.is_global && (
                  <button onClick={(e) => { e.stopPropagation(); del(l.id); }} className="btn-ghost p-2 hover:!text-tv-red hover:!border-tv-red/40" title="Delete" data-testid={`delete-my-list-${l.id}`}><Trash2 size={15} /></button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
